import { useState, useRef } from 'react';

import { observer } from 'mobx-react-lite';
import { clone } from 'mobx-state-tree';
import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { FormLabel } from '@components/form-label';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';
import { validateEmail } from '@utils/validators';

import { Header } from 'components/headers';
import { PageContent } from 'components/Page';
import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';

export const Details = observer(() => {
  const { userStore } = useStore();
  const fileInputRef = useRef(null);
  const me = useAuthenticatedUser();
  const user = userStore.items.get(me.id);
  const [clonedUser, setClonedUser] = useState(clone(user));
  const { api, t, lang, setLanguage } = useStore();
  const { showError, showSuccess } = useToaster();

  const [state, setState] = useState({
    file: null,
    errors: {},
    saving: false,
    displayFile: null,
  });
  const dirty =
    user.email !== clonedUser.email ||
    user.name !== clonedUser.name ||
    state.file !== null ||
    user.language !== clonedUser.language;

  function clearError(which) {
    if (Object.keys(state.errors).length) {
      let errors = { ...state.errors };
      delete errors[which];
      setState((old) => ({ ...old, errors }));
    }
  }

  function clickUpload() {
    fileInputRef.current.click();
  }

  function setName(name) {
    clonedUser.setName(name);
    clearError('name');
  }

  function setEmail(email) {
    clonedUser.setEmail(email);
    clearError('email');
  }

  async function changeFile(e) {
    const file = e.target.files.length > 0 ? e.target.files[0] : null;
    let displayFile = null;
    try {
      displayFile = await getBase64(file);
    } catch (e) {}
    setState((old) => ({ ...old, file, displayFile }));
  }

  function reset() {
    if (state.saving || !dirty) return;
    fileInputRef.current.value = '';
    setClonedUser(clone(user));
    user.setLanguage(lang);
    setState((old) => ({ ...old, file: null, errors: {}, displayFile: null }));
  }

  async function saveUser() {
    if (!dirty) {
      reset();
      showSuccess(t('user.profile.update.success'));
      return;
    }
    const { errors, file } = state;
    setState((old) => ({ ...old, saving: true }));

    let data = new FormData();
    if (file) {
      data.append('picture', file);
    }

    if (clonedUser.name !== user.name) {
      data.append('name', clonedUser.name);
    }

    if (clonedUser.email !== user.email) {
      const valid = validateEmail(clonedUser.email);
      if (valid) {
        data.append('email', clonedUser.email);
      } else {
        setState((old) => ({
          ...old,
          errors: { ...errors, email: t('user.profile.error.email') },
          saving: false,
        }));
        return;
      }
    }

    if (clonedUser.language !== user.language) {
      data.append('language', clonedUser.language);
    }

    const res = await api.users.updateSelf(data);
    if (res.error) {
      let str = '';
      Object.keys(res.data).forEach((k) => {
        str += res.data[k].join(' ');
      });
      showError(t('user.profile.error.generic', str || ['']));
      setState((old) => ({
        ...old,
        errors: {
          name: res.data.name ? res.data.name : false,
          email: res.data.email ? res.data.email : false,
        },
      }));
    } else {
      setLanguage(clonedUser.language);
      showSuccess(t('user.profile.update.success'));
      setState((old) => ({ ...old, file: null }));
      user.setName(clonedUser.name);
      user.setEmail(clonedUser.email);
      user.setLanguage(clonedUser.language);
    }

    setState((old) => ({ ...old, saving: false }));
  }

  const { errors, file, saving, displayFile } = state;
  if (!clonedUser) return null;

  return (
    <>
      <Header
        // @ts-ignore
        mobileTitle={me.name}
        breadcrumbs={<Breadcrumb items={[{ label: me.name }]} />}
      />
      <PageContent style={{ overflowY: 'auto' }}>
        <ProfileContainer>
          <ProfileImage>
            <Avatar
              src={displayFile ? displayFile : user.picture}
              name={clonedUser.name}
              size="xl"
            />
            {user.id === me.id && (
              <div>
                <Stack space={1}>
                  <FormLabel text={t('user.profile.label.image')} />
                  <ProfileImageContainer>
                    <UploadButton onClick={clickUpload}>
                      {t('user.profile.image.choose')}
                    </UploadButton>
                    <Text
                      font="regular14"
                      color="textHelper"
                      text={file ? file.name : t('user.profile.image.nofile')}
                    />
                  </ProfileImageContainer>
                </Stack>
                <input
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  name="file"
                  type="file"
                  accept="image/*"
                  onChange={changeFile}
                />
              </div>
            )}
          </ProfileImage>
          <Stack space={6}>
            <Stack space={4}>
              <TextField
                label={t('user.profile.label.name')}
                name="name"
                defaultValue={clonedUser.name}
                isDisabled={user.id !== me.id || saving}
                onChange={setName}
                error={errors.name}
              />
              <TextField
                label={t('user.profile.label.email')}
                name="email"
                defaultValue={clonedUser.email}
                isDisabled={user.id !== me.id || saving}
                onChange={setEmail}
                error={errors.email}
              />
              {user.id === me.id && (
                <>
                  <Select
                    key={saving}
                    isClearable={false}
                    isSearchable={false}
                    label={t('user.profile.label.language')}
                    options={[
                      { value: 'en', label: t('user.profile.label.language.english') },
                      { value: 'is', label: t('user.profile.label.language.icelandic') },
                    ]}
                    defaultValue={clonedUser.language}
                    onChange={clonedUser.setLanguage}
                    isDisabled={saving}
                  />
                </>
              )}
            </Stack>
            {user.id === me.id && (
              <ButtonGroup align="right">
                <Button
                  disabled={!dirty || saving}
                  onClick={!dirty ? null : saveUser}
                  text={t('user.profile.button.submit')}
                />
              </ButtonGroup>
            )}
          </Stack>
        </ProfileContainer>
      </PageContent>
    </>
  );
});

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

const ProfileContainer = styled.div`
  max-width: 380px;
  margin: 0 auto;
`;

const ProfileImage = styled.div`
  display: flex;
  align-items: center;
  margin: 30px 0;
  > div:nth-child(1) {
    margin-right: 20px;
  }
`;

const UploadButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  padding: 3px 8px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #bbb;
  :hover {
    cursor: pointer;
    background-color: rgba(255, 255, 255, 1);
  }
`;

const ProfileImageContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 2,
  align: 'center',
})({
  '@media(max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});
